
import { useState } from "react";
import { toast } from "sonner";
import { logger } from "@/lib/logger";
import { SlackTestResponse } from "../types";
import { slackEdgeApiClient } from "@/api";

/**
 * Hook for testing Slack connection
 */
export const useConnectionTester = () => {
  const [isTestingConnection, setIsTestingConnection] = useState(false);

  const testConnection = async (webhookUrl: string, slackToken: string): Promise<boolean> => {
    if (!webhookUrl || !slackToken) {
      toast.error("Webhook URL and Slack token are required for testing");
      return false;
    }

    setIsTestingConnection(true);
    
    try {
      logger.info({
        message: "Testing Slack connection",
        context: { webhookUrl: webhookUrl.substring(0, 15) + '...' }
      });
      
      // Call the Slack Edge Function endpoint to test the connection
      const data = await slackEdgeApiClient.testConnection(webhookUrl, slackToken);
      
      // Handle successful response
      logger.info({
        message: "Slack connection test completed",
        context: { success: data?.status === 'success' }
      });
      
      if (data?.status === 'success') {
        toast.success("Slack connection test successful");
        return true;
      } else {
        toast.error(`Slack connection test failed: ${data?.error || "Unknown error"}`);
        return false;
      }
    } catch (error) {
      logger.error({
        message: "Exception testing Slack connection",
        error: error instanceof Error ? error : new Error(String(error))
      });
      
      toast.error("Failed to test Slack connection");
      return false;
    } finally {
      setIsTestingConnection(false);
    }
  };
  
  return {
    testConnection,
    isTestingConnection
  };
};
